/*
 * MySiteCare
 *
 * Copyright © 2026 Jeanjen Sisoy Luberio
 * doing business as MySiteCare Website Application Services.
 * All rights reserved.
 *
 * DTI Business Name No. 7934250
 * https://mysitecare.eu
 *
 * This file contains original proprietary MySiteCare source code,
 * except for third-party components identified under their
 * respective licenses.
 */
import axios from "axios";

// The deployment supplies the public backend origin through the environment.
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;
export const backendConfigured = Boolean(BACKEND_URL);

export async function initializeComplianceVault(token) {
  const res = await axios.post(`${API}/admin/compliance/initialize`, {}, { headers: { Authorization: `Bearer ${token}` } });
  return res.data;
}

export async function fetchComplianceVault(token) {
  const res = await axios.get(`${API}/admin/compliance/dashboard`, { headers: { Authorization: `Bearer ${token}` } });
  return res.data;
}

export async function updateComplianceRequirement(token, id, payload) {
  const res = await axios.patch(`${API}/admin/compliance/requirements/${id}`, payload, { headers: { Authorization: `Bearer ${token}` } });
  return res.data;
}

export async function fetchDetectedRegion() {
  const res = await axios.get(`${API}/region`, { headers: { "Cache-Control": "no-store" } });
  return res.data;
}

export async function submitContact(payload) {
  const res = await axios.post(`${API}/contact`, payload);
  return res.data;
}

export async function fetchContacts(token) {
  const res = await axios.get(`${API}/contact`, { headers: { Authorization: `Bearer ${token}` } });
  return res.data;
}

export async function submitConceptCorrectionRequest(payload) {
  const res = await axios.post(`${API}/concept-sites/correction-requests`, payload);
  return res.data;
}

export async function fetchBusinessReviews(businessSlug) {
  const res = await axios.get(`${API}/businesses/${businessSlug}/reviews`);
  return res.data;
}

export async function fetchReviewIntegrations(businessSlug) {
  const res = await axios.get(`${API}/businesses/${businessSlug}/review-integrations`);
  return res.data;
}

export async function submitBusinessReview(businessSlug, payload) {
  const res = await axios.post(`${API}/businesses/${businessSlug}/reviews`, payload);
  return res.data;
}

export async function fetchReviewQueue(adminKey, status = "pending") {
  const res = await axios.get(`${API}/admin/reviews`, {
    params: { status },
    headers: { "X-Review-Admin-Key": adminKey },
  });
  return res.data;
}

export async function moderateBusinessReview(adminKey, reviewId, status) {
  const res = await axios.patch(`${API}/admin/reviews/${reviewId}`, { status }, {
    headers: { "X-Review-Admin-Key": adminKey },
  });
  return res.data;
}

// The one and only account-creation entry point — no separate owner/admin signup,
// setup key, or bootstrap step. The backend alone decides (from the submitted
// email) whether the new account is an owner or an ordinary customer; the response
// says which (`kind`) so the caller knows which session to start.
export async function registerAccount(email, password, displayName) {
  const res = await axios.post(`${API}/auth/register`, { email, password, display_name: displayName || undefined });
  return res.data;
}

export async function adminLogin(email, password) {
  const res = await axios.post(`${API}/auth/login`, { email, password });
  return res.data;
}

export async function fetchAdminMe(token) {
  const res = await axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } });
  return res.data;
}

export async function fetchPublicBusinessSlugs() {
  const res = await axios.get(`${API}/business-status`);
  return res.data.public_slugs;
}

const authHeaders = (token) => ({ headers: { Authorization: `Bearer ${token}` } });

export async function fetchAdminSites(token, { status, category, priority, search } = {}) {
  const res = await axios.get(`${API}/admin/sites`, {
    ...authHeaders(token),
    params: { status, category, priority, search },
  });
  return res.data;
}

export async function fetchAdminSite(token, slug) {
  const res = await axios.get(`${API}/admin/sites/${slug}`, authHeaders(token));
  return res.data;
}

export async function patchAdminSite(token, slug, updates) {
  const res = await axios.patch(`${API}/admin/sites/${slug}`, updates, authHeaders(token));
  return res.data;
}

export async function changeSiteStatus(token, slug, newStatus, note, rejectionReason) {
  const res = await axios.post(
    `${API}/admin/sites/${slug}/status`,
    { new_status: newStatus, note, rejection_reason: rejectionReason },
    authHeaders(token),
  );
  return res.data;
}

export async function fetchSiteHistory(token, slug) {
  const res = await axios.get(`${API}/admin/sites/${slug}/history`, authHeaders(token));
  return res.data;
}

// ---------- Sales CRM (Phase 4) ----------
export async function fetchSalesPipeline(token, { stage, category, priority, plan, channel, followUp, search, sort } = {}) {
  const res = await axios.get(`${API}/admin/sales`, {
    ...authHeaders(token),
    params: { stage, category, priority, plan, channel, follow_up: followUp, search, sort },
  });
  return res.data;
}

export async function fetchSalesRecord(token, slug) {
  const res = await axios.get(`${API}/admin/sales/${slug}`, authHeaders(token));
  return res.data;
}

export async function patchSalesRecord(token, slug, updates) {
  const res = await axios.patch(`${API}/admin/sales/${slug}`, updates, authHeaders(token));
  return res.data;
}

export async function changeSalesStage(token, slug, newStage, note, lostReason, lostReasonText) {
  const res = await axios.post(
    `${API}/admin/sales/${slug}/stage`,
    { new_stage: newStage, note, lost_reason: lostReason, lost_reason_text: lostReasonText },
    authHeaders(token),
  );
  return res.data;
}

export async function addSalesActivity(token, slug, activity) {
  const res = await axios.post(`${API}/admin/sales/${slug}/activity`, activity, authHeaders(token));
  return res.data;
}

// ---------- Client onboarding & go-live workflow (Phase 6) ----------
export async function fetchOnboarding(token, slug) {
  const res = await axios.get(`${API}/admin/onboarding/${slug}`, authHeaders(token));
  return res.data;
}

export async function patchBusinessVerification(token, slug, items) {
  const res = await axios.patch(`${API}/admin/onboarding/${slug}/business-verification`, { items }, authHeaders(token));
  return res.data;
}

export async function patchClientContact(token, slug, updates) {
  const res = await axios.patch(`${API}/admin/onboarding/${slug}/client-contact`, updates, authHeaders(token));
  return res.data;
}

export async function patchContentApproval(token, slug, items) {
  const res = await axios.patch(`${API}/admin/onboarding/${slug}/content-approval`, { items }, authHeaders(token));
  return res.data;
}

export async function addRequestedChange(token, slug, change) {
  const res = await axios.post(`${API}/admin/onboarding/${slug}/requested-changes`, change, authHeaders(token));
  return res.data;
}

export async function patchRequestedChange(token, slug, changeId, updates) {
  const res = await axios.patch(`${API}/admin/onboarding/${slug}/requested-changes/${changeId}`, updates, authHeaders(token));
  return res.data;
}

export async function patchCommercial(token, slug, updates) {
  const res = await axios.patch(`${API}/admin/onboarding/${slug}/commercial`, updates, authHeaders(token));
  return res.data;
}

export async function patchDomain(token, slug, updates) {
  const res = await axios.patch(`${API}/admin/onboarding/${slug}/domain`, updates, authHeaders(token));
  return res.data;
}

export async function patchContactRouting(token, slug, updates) {
  const res = await axios.patch(`${API}/admin/onboarding/${slug}/contact-routing`, updates, authHeaders(token));
  return res.data;
}

export async function patchTechnical(token, slug, items, indexingStatus) {
  const res = await axios.patch(`${API}/admin/onboarding/${slug}/technical`, { items, indexing_status: indexingStatus }, authHeaders(token));
  return res.data;
}

export async function patchFinalApproval(token, slug, updates) {
  const res = await axios.patch(`${API}/admin/onboarding/${slug}/final-approval`, updates, authHeaders(token));
  return res.data;
}

export async function addOnboardingOverride(token, slug, blockerKey, reason) {
  const res = await axios.post(`${API}/admin/onboarding/${slug}/override`, { blocker_key: blockerKey, reason }, authHeaders(token));
  return res.data;
}

export async function goLive(token, slug, confirm) {
  const res = await axios.post(`${API}/admin/onboarding/${slug}/go-live`, { confirm }, authHeaders(token));
  return res.data;
}

export async function patchHandoff(token, slug, updates) {
  const res = await axios.patch(`${API}/admin/onboarding/${slug}/handoff`, updates, authHeaders(token));
  return res.data;
}

export async function addPostHandoverRequest(token, slug, payload) {
  const res = await axios.post(`${API}/admin/onboarding/${slug}/post-handover-requests`, payload, authHeaders(token));
  return res.data;
}

export async function patchPostHandoverRequest(token, slug, requestId, updates) {
  const res = await axios.patch(`${API}/admin/onboarding/${slug}/post-handover-requests/${requestId}`, updates, authHeaders(token));
  return res.data;
}

export async function fetchProposals(token, slug) {
  const res = await axios.get(`${API}/admin/proposals/${slug}`, authHeaders(token));
  return res.data;
}

export async function createProposal(token, payload) {
  const res = await axios.post(`${API}/admin/proposals`, payload, authHeaders(token));
  return res.data;
}

export async function patchProposal(token, proposalId, updates) {
  const res = await axios.patch(`${API}/admin/proposals/detail/${proposalId}`, updates, authHeaders(token));
  return res.data;
}

export async function changeProposalStatus(token, proposalId, newStatus, note) {
  const res = await axios.post(`${API}/admin/proposals/detail/${proposalId}/status`, { new_status: newStatus, note }, authHeaders(token));
  return res.data;
}

export async function fetchAgreements(token, slug) {
  const res = await axios.get(`${API}/admin/agreements/${slug}`, authHeaders(token));
  return res.data;
}

export async function createAgreement(token, payload) {
  const res = await axios.post(`${API}/admin/agreements`, payload, authHeaders(token));
  return res.data;
}

export async function changeAgreementStatus(token, agreementId, newStatus, note) {
  const res = await axios.post(`${API}/admin/agreements/detail/${agreementId}/status`, { new_status: newStatus, note }, authHeaders(token));
  return res.data;
}

export async function patchAgreementLegalReview(token, agreementId, status, note) {
  const res = await axios.patch(`${API}/admin/agreements/detail/${agreementId}/legal-review`, { status, note }, authHeaders(token));
  return res.data;
}

export async function fetchCommercialConfig(token) {
  const res = await axios.get(`${API}/admin/commercial-config`, authHeaders(token));
  return res.data;
}

export async function fetchCommercialDocumentHistory(token, slug) {
  const res = await axios.get(`${API}/admin/commercial-documents/${slug}/history`, authHeaders(token));
  return res.data;
}

// ---------- Billing: customer portal (Phase B/I) ----------
export async function customerLogin(email, password) {
  const res = await axios.post(`${API}/customer/auth/login`, { email, password });
  return res.data;
}

export async function fetchCustomerMe(token) {
  const res = await axios.get(`${API}/customer/auth/me`, authHeaders(token));
  return res.data;
}

export async function requestCustomerPasswordReset(email) {
  const res = await axios.post(`${API}/customer/auth/password-reset/request`, { email });
  return res.data;
}

export async function confirmCustomerPasswordReset(token, newPassword) {
  const res = await axios.post(`${API}/customer/auth/password-reset/confirm`, { token, new_password: newPassword });
  return res.data;
}

export async function verifyCustomerEmail(token) {
  const res = await axios.post(`${API}/customer/auth/verify-email`, { token });
  return res.data;
}

export async function requestCustomerEmailVerification(sessionToken) {
  const res = await axios.post(`${API}/customer/auth/verification/request`, {}, authHeaders(sessionToken));
  return res.data;
}

export async function saveCustomerRegionPreference(token, region) {
  const res = await axios.put(`${API}/customer/region-preference`, { region }, authHeaders(token));
  return res.data;
}

export async function fetchCustomerWebsites(token) {
  const res = await axios.get(`${API}/customer/websites`, authHeaders(token));
  return res.data;
}

export async function selectCustomerWebsite(token, slug) {
  const res = await axios.post(`${API}/customer/websites/${slug}/select`, {}, authHeaders(token));
  return res.data;
}

export async function fetchCustomerWebsiteBilling(token, slug) {
  const res = await axios.get(`${API}/customer/websites/${slug}/billing`, authHeaders(token));
  return res.data;
}

export async function fetchCustomerInvoices(token) {
  const res = await axios.get(`${API}/customer/invoices`, authHeaders(token));
  return res.data;
}

export async function fetchCustomerSupportTickets(token) {
  const res = await axios.get(`${API}/customer/support/tickets`, authHeaders(token));
  return res.data;
}

export async function createCustomerSupportTicket(token, payload) {
  const res = await axios.post(`${API}/customer/support/tickets`, payload, authHeaders(token));
  return res.data;
}

export async function fetchCustomerSupportTicket(token, ticketId) {
  const res = await axios.get(`${API}/customer/support/tickets/${ticketId}`, authHeaders(token));
  return res.data;
}

export async function replyCustomerSupportTicket(token, ticketId, message) {
  const res = await axios.post(`${API}/customer/support/tickets/${ticketId}/replies`, { message }, authHeaders(token));
  return res.data;
}

export async function fetchCustomerPayments(token) {
  const res = await axios.get(`${API}/customer/payments`, authHeaders(token));
  return res.data;
}

export async function validateWebsiteAccessCode(token, code) {
  const res = await axios.post(`${API}/customer/website-code/validate`, { code }, authHeaders(token));
  return res.data;
}

export async function fetchCheckoutQuote(token, payload) {
  const res = await axios.post(`${API}/customer/checkout/quote`, payload, authHeaders(token));
  return res.data;
}

export async function createCustomerOrder(token, payload) {
  const res = await axios.post(`${API}/customer/orders`, payload, authHeaders(token));
  return res.data;
}

export async function createCustomerCheckout(token, orderId) {
  const res = await axios.post(`${API}/customer/orders/${orderId}/checkout`, {}, authHeaders(token));
  return res.data;
}

export async function simulateCustomerTestPayment(token, orderId, outcome) {
  const res = await axios.post(`${API}/customer/orders/${orderId}/test-payment`, { outcome }, authHeaders(token));
  return res.data;
}

export async function fetchCustomerOrder(token, orderId) {
  const res = await axios.get(`${API}/customer/orders/${orderId}`, authHeaders(token));
  return res.data;
}

export async function fetchCustomerInvoice(token, invoiceId) {
  const res = await axios.get(`${API}/customer/invoices/${invoiceId}`, authHeaders(token));
  return res.data;
}

export async function downloadCustomerInvoicePdf(token, invoiceId, invoiceNumber) {
  const res = await axios.get(`${API}/customer/invoices/${invoiceId}/download`, { ...authHeaders(token), responseType: "blob" });
  const url = URL.createObjectURL(res.data);
  const link = document.createElement("a");
  link.href = url;
  link.download = `${invoiceNumber || "mysitecare-invoice"}.pdf`;
  link.click();
  URL.revokeObjectURL(url);
}

export async function fetchCustomerInvoiceEmailPreview(token, invoiceId) {
  const res = await axios.get(`${API}/customer/invoices/${invoiceId}/email-preview`, authHeaders(token));
  return res.data;
}

export async function resendCustomerInvoiceEmail(token, invoiceId) {
  const res = await axios.post(`${API}/customer/invoices/${invoiceId}/resend-email`, {}, authHeaders(token));
  return res.data;
}

export async function cancelCustomerSubscription(token, subscriptionId) {
  const res = await axios.post(`${API}/customer/subscriptions/${subscriptionId}/cancel`, {}, authHeaders(token));
  return res.data;
}

// ---------- Billing: admin finance / BIR tax (Phase H/J/K/M) ----------
export async function fetchBillingOverview(token, includeTest = false) {
  const res = await axios.get(`${API}/admin/billing/overview`, { ...authHeaders(token), params: { include_test: includeTest || undefined } });
  return res.data;
}

export async function fetchBillingDiagnostics(token) {
  const res = await axios.get(`${API}/admin/billing/diagnostics`, authHeaders(token));
  return res.data;
}

export async function fetchBillingCustomers(token) {
  const res = await axios.get(`${API}/admin/billing/customers`, authHeaders(token));
  return res.data;
}

export async function fetchBillingCustomerDetail(token, customerId) {
  const res = await axios.get(`${API}/admin/billing/customers/${customerId}`, authHeaders(token));
  return res.data;
}

export async function createBillingCustomer(token, payload) {
  const res = await axios.post(`${API}/admin/billing/customers`, payload, authHeaders(token));
  return res.data;
}

export async function createBillingCustomerUser(token, payload) {
  const res = await axios.post(`${API}/admin/billing/customer-users`, payload, authHeaders(token));
  return res.data;
}

export async function createBillingMembership(token, payload) {
  const res = await axios.post(`${API}/admin/billing/memberships`, payload, authHeaders(token));
  return res.data;
}

export async function revokeBillingMembership(token, membershipId) {
  const res = await axios.delete(`${API}/admin/billing/memberships/${membershipId}`, authHeaders(token));
  return res.data;
}

export async function fetchBillingPlans(token) {
  const res = await axios.get(`${API}/admin/billing/plans`, authHeaders(token));
  return res.data;
}

export async function createBillingOrder(token, payload) {
  const res = await axios.post(`${API}/admin/billing/orders`, payload, authHeaders(token));
  return res.data;
}

export async function fetchBillingOrders(token, status) {
  const res = await axios.get(`${API}/admin/billing/orders`, { ...authHeaders(token), params: { status } });
  return res.data;
}

export async function createBillingInvoiceForOrder(token, orderId) {
  const res = await axios.post(`${API}/admin/billing/orders/${orderId}/invoice`, {}, authHeaders(token));
  return res.data;
}

export async function fetchBillingInvoices(token) {
  const res = await axios.get(`${API}/admin/billing/invoices`, authHeaders(token));
  return res.data;
}

export async function voidBillingInvoice(token, invoiceId, reason) {
  const res = await axios.post(`${API}/admin/billing/invoices/${invoiceId}/void`, null, { ...authHeaders(token), params: { reason } });
  return res.data;
}

export async function fetchBillingPayments(token, includeTest = false) {
  const res = await axios.get(`${API}/admin/billing/payments`, { ...authHeaders(token), params: { include_test: includeTest || undefined } });
  return res.data;
}

export async function fetchBillingNotifications(token) {
  const res = await axios.get(`${API}/admin/billing/notifications`, authHeaders(token));
  return res.data;
}

export async function retryBillingNotification(token, paymentId) {
  const res = await axios.post(`${API}/admin/billing/notifications/${paymentId}/retry`, {}, authHeaders(token));
  return res.data;
}

export async function recordManualPayment(token, payload) {
  const res = await axios.post(`${API}/admin/billing/payments/manual`, payload, authHeaders(token));
  return res.data;
}

export async function createBillingRefund(token, paymentId, payload) {
  const res = await axios.post(`${API}/admin/billing/payments/${paymentId}/refund`, payload, authHeaders(token));
  return res.data;
}

export async function fetchBillingSubscriptions(token) {
  const res = await axios.get(`${API}/admin/billing/subscriptions`, authHeaders(token));
  return res.data;
}

export async function fetchBillingReconciliation(token, includeTest = false) {
  const res = await axios.get(`${API}/admin/billing/reconciliation`, { ...authHeaders(token), params: { include_test: includeTest || undefined } });
  return res.data;
}

export async function fetchBillingLedger(token, period, businessId, fmt = "json", extra = {}) {
  const res = await axios.get(`${API}/admin/billing/ledger`, { ...authHeaders(token), params: { period, business_id: businessId, fmt, ...extra } });
  return res.data;
}

// Per-account statements: opening balance, every entry with its running balance,
// total debits/credits and closing balance — all computed by the backend.
export async function fetchLedgerStatement(token, businessId, { period, quarter, account, currency } = {}) {
  const res = await axios.get(`${API}/admin/billing/ledger/statement`, {
    ...authHeaders(token), params: { business_id: businessId, period: period || undefined, quarter: quarter || undefined, account: account || undefined, currency: currency || undefined },
  });
  return res.data;
}

export async function fetchBillingTrialBalance(token, period, businessId, fmt = "json") {
  const res = await axios.get(`${API}/admin/billing/ledger/trial-balance`, { ...authHeaders(token), params: { period, business_id: businessId, fmt } });
  return res.data;
}

export async function fetchBillingExpenses(token, businessId) {
  const res = await axios.get(`${API}/admin/billing/expenses`, { ...authHeaders(token), params: { business_id: businessId } });
  return res.data;
}

export async function createBillingExpense(token, payload) {
  const res = await axios.post(`${API}/admin/billing/expenses`, payload, authHeaders(token));
  return res.data;
}

// Chart of Accounts (Finance Phase 2, 2026-09-22) — business_id is REQUIRED (no
// default) for every call here, matching the backend's own commitment that new
// Finance endpoints never silently fall back to "mysitecare".
export async function fetchAccounts(token, businessId, status) {
  const res = await axios.get(`${API}/admin/billing/accounts`, { ...authHeaders(token), params: { business_id: businessId, status } });
  return res.data;
}

export async function createAccount(token, payload) {
  const res = await axios.post(`${API}/admin/billing/accounts`, payload, authHeaders(token));
  return res.data;
}

export async function updateAccount(token, accountId, payload) {
  const res = await axios.patch(`${API}/admin/billing/accounts/${accountId}`, payload, authHeaders(token));
  return res.data;
}

export async function archiveAccount(token, accountId, businessId) {
  const res = await axios.post(`${API}/admin/billing/accounts/${accountId}/archive`, {}, { ...authHeaders(token), params: { business_id: businessId } });
  return res.data;
}

export async function activateAccount(token, accountId, businessId) {
  const res = await axios.post(`${API}/admin/billing/accounts/${accountId}/activate`, {}, { ...authHeaders(token), params: { business_id: businessId } });
  return res.data;
}

export async function deleteAccount(token, accountId, businessId) {
  const res = await axios.delete(`${API}/admin/billing/accounts/${accountId}`, { ...authHeaders(token), params: { business_id: businessId } });
  return res.data;
}

export async function fetchBillingReport(token, reportName, params = {}) {
  const res = await axios.get(`${API}/admin/billing/reports/${reportName}`, { ...authHeaders(token), params });
  return res.data;
}

// --- Books & Reports (Finance Phase 4, 2026-09-23) ------------------------------
export async function fetchGeneralJournal(token, period, businessId, fmt = "json", extra = {}) {
  const res = await axios.get(`${API}/admin/billing/ledger/general-journal`, { ...authHeaders(token), params: { period, business_id: businessId, fmt, ...extra } });
  return res.data;
}

export async function fetchCashReceipts(token, period, businessId, fmt = "json") {
  const res = await axios.get(`${API}/admin/billing/reports/cash-receipts`, { ...authHeaders(token), params: { period, business_id: businessId, fmt } });
  return res.data;
}

export async function fetchCashDisbursements(token, period, businessId, fmt = "json") {
  const res = await axios.get(`${API}/admin/billing/reports/cash-disbursements`, { ...authHeaders(token), params: { period, business_id: businessId, fmt } });
  return res.data;
}

export async function fetchLedgerSource(token, sourceType, sourceId) {
  const res = await axios.get(`${API}/admin/billing/ledger/source/${sourceType}/${sourceId}`, authHeaders(token));
  return res.data;
}

export async function createAdjustment(token, payload) {
  const res = await axios.post(`${API}/admin/billing/ledger/adjustments`, payload, authHeaders(token));
  return res.data;
}

export async function reverseJournal(token, journalId, reason, businessId) {
  const res = await axios.post(`${API}/admin/billing/ledger/journal/${journalId}/reverse`, null, { ...authHeaders(token), params: { reason, business_id: businessId } });
  return res.data;
}

// Downloads a {content, filename} (text) or {content_base64, filename, mime}
// (binary) export envelope from _formatted_report as an actual browser file
// download — one shared path for every CSV/XLSX/PDF export in Books & Reports.
export function downloadReportPayload(payload) {
  const blob = payload.content_base64
    ? new Blob([Uint8Array.from(atob(payload.content_base64), (c) => c.charCodeAt(0))], { type: payload.mime || "application/octet-stream" })
    : new Blob([payload.content], { type: payload.mime || "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = payload.filename;
  a.click();
  URL.revokeObjectURL(url);
}

export async function fetchTaxProfile(token) {
  const res = await axios.get(`${API}/admin/billing/tax/profile`, authHeaders(token));
  return res.data;
}

export async function patchTaxProfile(token, payload) {
  const res = await axios.patch(`${API}/admin/billing/tax/profile`, payload, authHeaders(token));
  return res.data;
}

export async function fetchTaxElections(token) {
  const res = await axios.get(`${API}/admin/billing/tax/elections`, authHeaders(token));
  return res.data;
}

export async function upsertTaxElection(token, payload) {
  const res = await axios.post(`${API}/admin/billing/tax/elections`, payload, authHeaders(token));
  return res.data;
}

export async function fetchTaxEstimate(token, year) {
  const res = await axios.get(`${API}/admin/billing/tax/estimate`, { ...authHeaders(token), params: { year } });
  return res.data;
}

export async function fetchIncomeClassifications(token) {
  const res = await axios.get(`${API}/admin/billing/tax/income-classification`, authHeaders(token));
  return res.data;
}

export async function upsertIncomeClassification(token, payload) {
  const res = await axios.post(`${API}/admin/billing/tax/income-classification`, payload, authHeaders(token));
  return res.data;
}

export async function fetchTaxBusinesses(token) {
  const res = await axios.get(`${API}/admin/billing/tax/businesses`, authHeaders(token));
  return res.data;
}
export async function fetchScopedTaxProfile(token, businessId) {
  const res = await axios.get(`${API}/admin/billing/tax/businesses/${businessId}/profile`, authHeaders(token));
  return res.data;
}
export async function patchScopedTaxProfile(token, businessId, payload) {
  const res = await axios.patch(`${API}/admin/billing/tax/businesses/${businessId}/profile`, payload, authHeaders(token));
  return res.data;
}
export async function fetchOrusPreparation(token, businessId, fmt = "json") {
  const res = await axios.get(`${API}/admin/billing/tax/businesses/${businessId}/orus-preparation`, { ...authHeaders(token), params: { fmt } });
  return res.data;
}
export async function fetchTaxpayerMasterProfile(token, businessId) {
  const res = await axios.get(`${API}/admin/billing/tax/businesses/${businessId}/master-profile`, authHeaders(token));
  return res.data;
}
export async function revealTaxpayerTin(token, businessId) {
  const res = await axios.get(`${API}/admin/billing/tax/businesses/${businessId}/profile/tin-reveal`, authHeaders(token));
  return res.data;
}
export async function fetchTaxPreparationWorksheet(token, year, fmt = "json", businessId = "mysitecare") {
  const res = await axios.get(`${API}/admin/billing/tax/preparation-worksheet`, { ...authHeaders(token), params: { year, fmt, business_id: businessId } });
  return res.data;
}

// --- Finance Document Center (Phase 5, 2026-09-26) -------------------------------
export async function fetchComplianceDocuments(token, businessId, linkKind, linkRef) {
  const res = await axios.get(`${API}/admin/billing/documents`, { ...authHeaders(token), params: { business_id: businessId, link_kind: linkKind || undefined, link_ref: linkRef || undefined } });
  return res.data;
}
export async function uploadComplianceDocument(token, businessId, file, meta = {}) {
  const form = new FormData();
  form.append("file", file);
  const res = await axios.post(`${API}/admin/billing/documents`, form, {
    ...authHeaders(token),
    params: { business_id: businessId, document_type: meta.documentType, tax_year: meta.taxYear || undefined, period: meta.period || undefined, notes: meta.notes || undefined },
  });
  return res.data;
}
export async function downloadComplianceDocument(token, businessId, docId, filename) {
  const res = await axios.get(`${API}/admin/billing/documents/${docId}/download`, { ...authHeaders(token), params: { business_id: businessId }, responseType: "blob" });
  const url = URL.createObjectURL(res.data);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}
export async function viewComplianceDocument(token, businessId, docId) {
  const res = await axios.get(`${API}/admin/billing/documents/${docId}/download`, { ...authHeaders(token), params: { business_id: businessId }, responseType: "blob" });
  window.open(URL.createObjectURL(res.data), "_blank", "noopener,noreferrer");
}
export async function editComplianceDocument(token, businessId, docId, meta) {
  const res = await axios.patch(`${API}/admin/billing/documents/${docId}`, meta, { ...authHeaders(token), params: { business_id: businessId } });
  return res.data;
}
export async function replaceComplianceDocument(token, businessId, docId, file) {
  const form = new FormData(); form.append("file", file);
  const res = await axios.post(`${API}/admin/billing/documents/${docId}/replace`, form, { ...authHeaders(token), params: { business_id: businessId } });
  return res.data;
}
export async function archiveComplianceDocument(token, businessId, docId) {
  const res = await axios.post(`${API}/admin/billing/documents/${docId}/archive`, {}, { ...authHeaders(token), params: { business_id: businessId } });
  return res.data;
}
export async function deleteComplianceDocument(token, businessId, docId) {
  const res = await axios.delete(`${API}/admin/billing/documents/${docId}`, { ...authHeaders(token), params: { business_id: businessId, confirm: true } });
  return res.data;
}
export async function saveScopedTaxElection(token, businessId, payload) {
  const res = await axios.post(`${API}/admin/billing/tax/businesses/${businessId}/elections`, payload, authHeaders(token));
  return res.data;
}
export async function fetchScopedTaxElection(token, businessId, taxYear) {
  const res = await axios.get(`${API}/admin/billing/tax/businesses/${businessId}/elections/${taxYear}`, authHeaders(token));
  return res.data;
}
export async function saveScopedIncomeClassification(token, businessId, payload) {
  const res = await axios.post(`${API}/admin/billing/tax/businesses/${businessId}/income-classifications`, payload, authHeaders(token));
  return res.data;
}
export async function fetchScopedIncomeClassification(token, businessId, taxYear) {
  const res = await axios.get(`${API}/admin/billing/tax/businesses/${businessId}/income-classifications/${taxYear}`, authHeaders(token));
  return res.data;
}
export async function fetchScopedFirstPeriod(token, businessId, year) {
  const res = await axios.get(`${API}/admin/billing/tax/businesses/${businessId}/first-period?year=${year}`, authHeaders(token));
  return res.data;
}
export async function saveScopedFirstPeriod(token, businessId, payload) {
  const res = await axios.put(`${API}/admin/billing/tax/businesses/${businessId}/first-period`, payload, authHeaders(token));
  return res.data;
}
export async function fetchScopedTaxTypes(token, businessId) {
  const res = await axios.get(`${API}/admin/billing/tax/businesses/${businessId}/tax-types`, authHeaders(token));
  return res.data;
}
export async function createScopedTaxType(token, businessId, payload) {
  const res = await axios.post(`${API}/admin/billing/tax/businesses/${businessId}/tax-types`, payload, authHeaders(token));
  return res.data;
}

export async function fetchTaxAutomationHealth(token, year) {
  const res = await axios.get(`${API}/admin/billing/tax/automation-health`, { ...authHeaders(token), params: { year } });
  return res.data;
}

export async function fetchTaxFilings(token, businessId = "mysitecare") {
  const res = await axios.get(`${API}/admin/billing/tax/filings`, { ...authHeaders(token), params: { business_id: businessId } });
  return res.data;
}

export async function createTaxFiling(token, payload) {
  const res = await axios.post(`${API}/admin/billing/tax/filings`, payload, authHeaders(token));
  return res.data;
}

export async function changeTaxFilingStatus(token, filingId, payload, businessId = "mysitecare") {
  const res = await axios.post(`${API}/admin/billing/tax/filings/${filingId}/status`, payload, { ...authHeaders(token), params: { business_id: businessId } });
  return res.data;
}

export async function finalizeTaxPackage(token, filingId, businessId = "mysitecare") {
  const res = await axios.post(`${API}/admin/billing/tax/filings/${filingId}/finalize-package`, {}, { ...authHeaders(token), params: { business_id: businessId } });
  return res.data;
}

export async function fetchMonthlyClose(token, period, businessId) {
  const res = await axios.get(`${API}/admin/billing/monthly-close/${period}`, { ...authHeaders(token), params: { business_id: businessId } });
  return res.data;
}

export async function listMonthlyClose(token, businessId) {
  const res = await axios.get(`${API}/admin/billing/monthly-close`, { ...authHeaders(token), params: { business_id: businessId } });
  return res.data;
}

export async function updateMonthlyCloseChecklist(token, period, item, done, businessId) {
  const res = await axios.patch(`${API}/admin/billing/monthly-close/${period}/checklist`, { item, done, business_id: businessId }, authHeaders(token));
  return res.data;
}

export async function closeMonth(token, period, businessId, notes) {
  const res = await axios.post(`${API}/admin/billing/monthly-close/${period}/close`, {}, { ...authHeaders(token), params: { business_id: businessId, notes: notes || undefined } });
  return res.data;
}

export async function reopenMonth(token, period, reason, businessId) {
  const res = await axios.post(`${API}/admin/billing/monthly-close/${period}/reopen`, null, { ...authHeaders(token), params: { reason, business_id: businessId } });
  return res.data;
}

export async function fetchMonthlyCloseSummary(token, period, businessId) {
  const res = await axios.get(`${API}/admin/billing/monthly-close/${period}/summary`, { ...authHeaders(token), params: { business_id: businessId } });
  return res.data;
}

export async function fetchBillingAuditLog(token, businessId) {
  const res = await axios.get(`${API}/admin/billing/audit-log`, { ...authHeaders(token), params: businessId ? { business_id: businessId } : {} });
  return res.data;
}

export async function fetchComplianceReminders(token, businessId) {
  const res = await axios.get(`${API}/admin/billing/compliance-reminders`, { ...authHeaders(token), params: { business_id: businessId } });
  return res.data;
}

export async function fetchSetupChecklist(token, businessId) {
  const res = await axios.get(`${API}/admin/billing/setup-checklist`, { ...authHeaders(token), params: { business_id: businessId } });
  return res.data;
}

// ---------------------------------------------------------------------------
// Website Owner Business Dashboard (site_ops_routes.py)
// ---------------------------------------------------------------------------
export async function fetchSiteCapabilities(token, slug) {
  const res = await axios.get(`${API}/customer/websites/${slug}/capabilities`, authHeaders(token));
  return res.data;
}

export async function fetchAdminSiteCapabilities(token, slug) {
  const res = await axios.get(`${API}/admin/sites/${slug}/capabilities`, authHeaders(token));
  return res.data;
}

export async function patchAdminSiteCapabilities(token, slug, capabilities) {
  const res = await axios.patch(`${API}/admin/sites/${slug}/capabilities`, { capabilities }, authHeaders(token));
  return res.data;
}

// Website Access (claim) Code — admin side. Never returns the raw code except from
// the explicit reveal/regenerate actions below (a deliberate click, not a page load).
export async function fetchWebsiteClaimCode(token, slug) {
  const res = await axios.get(`${API}/admin/websites/${slug}/claim-code`, authHeaders(token));
  return res.data;
}

export async function revealWebsiteClaimCode(token, slug) {
  const res = await axios.post(`${API}/admin/websites/${slug}/claim-code/reveal`, {}, authHeaders(token));
  return res.data;
}

export async function regenerateWebsiteClaimCode(token, slug) {
  const res = await axios.post(`${API}/admin/websites/${slug}/claim-code/regenerate`, {}, authHeaders(token));
  return res.data;
}

export async function revokeWebsiteClaimCode(token, slug) {
  const res = await axios.post(`${API}/admin/websites/${slug}/claim-code/revoke`, {}, authHeaders(token));
  return res.data;
}

// Website Access (claim) Code — customer side.
export async function claimWebsite(token, code) {
  const res = await axios.post(`${API}/customer/websites/claim`, { code }, authHeaders(token));
  return res.data;
}

export async function fetchSiteAvailability(token, slug) {
  const res = await axios.get(`${API}/customer/websites/${slug}/availability`, authHeaders(token));
  return res.data;
}

export async function patchSiteAvailability(token, slug, payload) {
  const res = await axios.patch(`${API}/customer/websites/${slug}/availability`, payload, authHeaders(token));
  return res.data;
}

export async function fetchSiteAppointments(token, slug, params = {}) {
  const res = await axios.get(`${API}/customer/websites/${slug}/appointments`, { ...authHeaders(token), params });
  return res.data;
}

export async function decideSiteAppointment(token, slug, appointmentId, payload) {
  const res = await axios.post(`${API}/customer/websites/${slug}/appointments/${appointmentId}/decision`, payload, authHeaders(token));
  return res.data;
}

export async function submitPublicAppointmentRequest(slug, payload) {
  const res = await axios.post(`${API}/public/sites/${slug}/appointments`, payload);
  return res.data;
}

export async function fetchSiteCustomers(token, slug, params = {}) {
  const res = await axios.get(`${API}/customer/websites/${slug}/customers`, { ...authHeaders(token), params });
  return res.data;
}

export async function fetchSiteCustomer(token, slug, customerId) {
  const res = await axios.get(`${API}/customer/websites/${slug}/customers/${customerId}`, authHeaders(token));
  return res.data;
}

export async function patchSiteCustomerStatus(token, slug, customerId, status) {
  const res = await axios.patch(`${API}/customer/websites/${slug}/customers/${customerId}`, { status }, authHeaders(token));
  return res.data;
}

export async function addSiteCustomerNote(token, slug, customerId, text) {
  const res = await axios.post(`${API}/customer/websites/${slug}/customers/${customerId}/notes`, { text }, authHeaders(token));
  return res.data;
}

export async function fetchSiteAnalytics(token, slug, range = "30d") {
  const res = await axios.get(`${API}/customer/websites/${slug}/analytics`, { ...authHeaders(token), params: { range } });
  return res.data;
}

export async function recordPublicPageview(slug) {
  try { await axios.post(`${API}/public/sites/${slug}/analytics/pageview`); } catch { /* never block the page over this */ }
}

export async function fetchSiteTaxProfile(token, slug) {
  const res = await axios.get(`${API}/customer/websites/${slug}/tax-profile`, authHeaders(token));
  return res.data;
}

export async function patchSiteTaxProfile(token, slug, payload) {
  const res = await axios.patch(`${API}/customer/websites/${slug}/tax-profile`, payload, authHeaders(token));
  return res.data;
}

export async function fetchSiteTaxObligations(token, slug, year) {
  const res = await axios.get(`${API}/customer/websites/${slug}/tax-obligations`, { ...authHeaders(token), params: { year } });
  return res.data;
}

export async function createSiteTaxObligation(token, slug, payload) {
  const res = await axios.post(`${API}/customer/websites/${slug}/tax-obligations`, payload, authHeaders(token));
  return res.data;
}

export async function completeSiteTaxObligation(token, slug, obligationId, payload) {
  const res = await axios.post(`${API}/customer/websites/${slug}/tax-obligations/${obligationId}/complete`, payload, authHeaders(token));
  return res.data;
}

// Books / compliance API: these calls only describe internal accounting and
// manual-book workflow state. They do not submit anything to a government system.
export async function fetchRegisteredBooks(token, businessId) {
  const res = await axios.get(`${API}/admin/billing/tax/businesses/${businessId}/registered-books`, authHeaders(token));
  return res.data;
}

export async function updateRegisteredBook(token, businessId, bookKey, payload) {
  const res = await axios.patch(`${API}/admin/billing/tax/businesses/${businessId}/registered-books/${bookKey}`, { ...payload, business_id: businessId }, authHeaders(token));
  return res.data;
}

export async function fetchPhysicalTranscriptionQueue(token, businessId, params = {}) {
  const res = await axios.get(`${API}/admin/billing/tax/businesses/${businessId}/physical-transcription-queue`, { ...authHeaders(token), params });
  return res.data;
}

export async function markPhysicalTranscriptionWritten(token, businessId, payload) {
  const res = await axios.post(`${API}/admin/billing/tax/businesses/${businessId}/physical-transcription-queue/mark-written`, { ...payload, business_id: businessId }, authHeaders(token));
  return res.data;
}

export async function correctPhysicalTranscription(token, businessId, journalId, payload) {
  const res = await axios.post(`${API}/admin/billing/tax/businesses/${businessId}/physical-transcription-queue/${journalId}/correct`, { ...payload, business_id: businessId }, authHeaders(token));
  return res.data;
}

export async function resolvePhysicalTranscriptionCorrection(token, businessId, journalId, payload) {
  const res = await axios.post(`${API}/admin/billing/tax/businesses/${businessId}/physical-transcription-queue/${journalId}/resolve-correction`, { ...payload, business_id: businessId }, authHeaders(token));
  return res.data;
}

export async function fetchPreOpeningItems(token, businessId, params = {}) {
  const res = await axios.get(`${API}/admin/billing/pre-opening-items`, { ...authHeaders(token), params: { ...params, business_id: businessId } });
  return res.data;
}

export async function createPreOpeningItem(token, payload) {
  const res = await axios.post(`${API}/admin/billing/pre-opening-items`, payload, authHeaders(token));
  return res.data;
}

export async function reviewPreOpeningItem(token, itemId, payload) {
  const res = await axios.patch(`${API}/admin/billing/pre-opening-items/${itemId}/review`, payload, authHeaders(token));
  return res.data;
}

export async function fetchFinanceScreenHelp(token, screenKey, businessId) {
  const res = await axios.get(`${API}/admin/billing/finance-help/${screenKey}`, { ...authHeaders(token), params: { business_id: businessId } });
  return res.data;
}

export async function fetchTaxObligations(token, businessId, taxYear) {
  const res = await axios.get(`${API}/admin/billing/tax/businesses/${businessId}/obligations`, { ...authHeaders(token), params: { tax_year: taxYear } });
  return res.data;
}

export async function fetchSiteTaxCalendar(token, slug, year) {
  const res = await axios.get(`${API}/customer/websites/${slug}/tax-calendar`, { ...authHeaders(token), params: { year } });
  return res.data;
}

export async function fetchAdminOperationsOverview(token) {
  const res = await axios.get(`${API}/admin/operations/overview`, authHeaders(token));
  return res.data;
}

export async function fetchAdminSiteTaxOverview(token) {
  const res = await axios.get(`${API}/admin/site-tax-overview`, authHeaders(token));
  return res.data;
}

export async function fetchAdminTaxCalendar(token, year) {
  const res = await axios.get(`${API}/admin/billing/tax/calendar`, { ...authHeaders(token), params: { year } });
  return res.data;
}

// ---------------------------------------------------------------------------
// Website Owner Commerce (site_commerce_routes.py)
// ---------------------------------------------------------------------------
export async function fetchSiteProducts(token, slug, params = {}) {
  const res = await axios.get(`${API}/customer/websites/${slug}/products`, { ...authHeaders(token), params });
  return res.data;
}

export async function createSiteProduct(token, slug, payload) {
  const res = await axios.post(`${API}/customer/websites/${slug}/products`, payload, authHeaders(token));
  return res.data;
}

export async function fetchSiteProduct(token, slug, productId) {
  const res = await axios.get(`${API}/customer/websites/${slug}/products/${productId}`, authHeaders(token));
  return res.data;
}

export async function patchSiteProduct(token, slug, productId, payload) {
  const res = await axios.patch(`${API}/customer/websites/${slug}/products/${productId}`, payload, authHeaders(token));
  return res.data;
}

export async function createSiteVariant(token, slug, productId, payload) {
  const res = await axios.post(`${API}/customer/websites/${slug}/products/${productId}/variants`, payload, authHeaders(token));
  return res.data;
}

export async function adjustSiteStock(token, slug, productId, payload) {
  const res = await axios.post(`${API}/customer/websites/${slug}/products/${productId}/stock-adjustment`, payload, authHeaders(token));
  return res.data;
}

export async function fetchSiteInventoryMovements(token, slug, params = {}) {
  const res = await axios.get(`${API}/customer/websites/${slug}/inventory/movements`, { ...authHeaders(token), params });
  return res.data;
}

export async function fetchSiteInventorySummary(token, slug) {
  const res = await axios.get(`${API}/customer/websites/${slug}/inventory/summary`, authHeaders(token));
  return res.data;
}

export async function fetchSiteOrders(token, slug, params = {}) {
  const res = await axios.get(`${API}/customer/websites/${slug}/orders`, { ...authHeaders(token), params });
  return res.data;
}

export async function createSiteOrder(token, slug, payload) {
  const res = await axios.post(`${API}/customer/websites/${slug}/orders`, payload, authHeaders(token));
  return res.data;
}

export async function fetchSiteOrder(token, slug, orderId) {
  const res = await axios.get(`${API}/customer/websites/${slug}/orders/${orderId}`, authHeaders(token));
  return res.data;
}

export async function changeSiteOrderStatus(token, slug, orderId, action, note) {
  const res = await axios.post(`${API}/customer/websites/${slug}/orders/${orderId}/status`, { action, note }, authHeaders(token));
  return res.data;
}

export async function updateSiteOrderPayment(token, slug, orderId, payload) {
  const res = await axios.post(`${API}/customer/websites/${slug}/orders/${orderId}/payment`, payload, authHeaders(token));
  return res.data;
}

export async function fetchSiteShipments(token, slug, params = {}) {
  const res = await axios.get(`${API}/customer/websites/${slug}/shipments`, { ...authHeaders(token), params });
  return res.data;
}

export async function createSiteShipment(token, slug, orderId, payload) {
  const res = await axios.post(`${API}/customer/websites/${slug}/orders/${orderId}/shipments`, payload, authHeaders(token));
  return res.data;
}

export async function changeSiteShipmentStatus(token, slug, shipmentId, action, note) {
  const res = await axios.post(`${API}/customer/websites/${slug}/shipments/${shipmentId}/status`, { action, note }, authHeaders(token));
  return res.data;
}

export async function fetchSiteCommerceSummary(token, slug) {
  const res = await axios.get(`${API}/customer/websites/${slug}/commerce-summary`, authHeaders(token));
  return res.data;
}

export async function fetchAdminCommerceOverview(token) {
  const res = await axios.get(`${API}/admin/commerce-overview`, authHeaders(token));
  return res.data;
}

// ---------------------------------------------------------------------------
// Public storefront (no auth — shopper-facing)
// ---------------------------------------------------------------------------
export async function fetchPublicCatalog(slug, params = {}) {
  const res = await axios.get(`${API}/public/sites/${slug}/catalog`, { params });
  return res.data;
}

export async function fetchPublicProduct(slug, productId) {
  const res = await axios.get(`${API}/public/sites/${slug}/catalog/${productId}`);
  return res.data;
}

export async function submitPublicCheckout(slug, payload) {
  const res = await axios.post(`${API}/public/sites/${slug}/checkout`, payload);
  return res.data;
}

// ---------------------------------------------------------------------------
// Standalone Website Handover (handover_routes.py)
// ---------------------------------------------------------------------------
export async function fetchCustomerHandover(token, slug) {
  const res = await axios.get(`${API}/customer/websites/${slug}/handover`, authHeaders(token));
  return res.data;
}

// The backend returns `download_url` as an absolute API path ("/api/handover/download/<token>").
// API already ends in "/api", so prefixing it with API produced ".../api/api/handover/..." —
// a 404 that made the Download My Website button silently do nothing.
export function handoverDownloadHref(downloadUrl) {
  return downloadUrl.startsWith("/api/") ? `${BACKEND_URL}${downloadUrl}` : `${API}${downloadUrl}`;
}

export async function downloadCustomerHandover(token, downloadUrl, slug) {
  const res = await axios.get(handoverDownloadHref(downloadUrl), { ...authHeaders(token), responseType: "blob" });
  const url = URL.createObjectURL(res.data);
  const link = document.createElement("a");
  link.href = url;
  link.download = `${slug || "mysitecare-website"}-handover.zip`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export async function fetchAdminHandoverList(token) {
  const res = await axios.get(`${API}/admin/handover`, authHeaders(token));
  return res.data;
}

export async function fetchAdminHandover(token, slug) {
  const res = await axios.get(`${API}/admin/handover/${slug}`, authHeaders(token));
  return res.data;
}

export async function prepareHandover(token, slug) {
  const res = await axios.post(`${API}/admin/handover/${slug}/prepare`, {}, authHeaders(token));
  return res.data;
}

export async function approveHandover(token, slug) {
  const res = await axios.post(`${API}/admin/handover/${slug}/approve`, {}, authHeaders(token));
  return res.data;
}

export async function generateHandoverDownloadLink(token, slug) {
  const res = await axios.post(`${API}/admin/handover/${slug}/generate-download-link`, {}, authHeaders(token));
  return res.data;
}

// ---------------------------------------------------------------------------
// Operations / Monitoring (monitoring_routes.py)
// ---------------------------------------------------------------------------
export async function fetchMonitoringOverview(token) {
  const res = await axios.get(`${API}/admin/monitoring/overview`, authHeaders(token));
  return res.data;
}

export async function fetchNeedsManualAction(token) {
  const res = await axios.get(`${API}/admin/monitoring/needs-manual-action`, authHeaders(token));
  return res.data;
}

export async function fetchMonitoringIncidents(token, params = {}) {
  const res = await axios.get(`${API}/admin/monitoring/incidents`, { ...authHeaders(token), params });
  return res.data;
}

export async function fetchMonitoringIncidentDetail(token, incidentId) {
  const res = await axios.get(`${API}/admin/monitoring/incidents/${incidentId}`, authHeaders(token));
  return res.data;
}

export async function resolveMonitoringIncident(token, incidentId, note) {
  const res = await axios.post(`${API}/admin/monitoring/incidents/${incidentId}/resolve`, { note: note || null }, authHeaders(token));
  return res.data;
}

export async function acknowledgeMonitoringIncident(token, incidentId) {
  const res = await axios.post(`${API}/admin/monitoring/incidents/${incidentId}/acknowledge`, {}, authHeaders(token));
  return res.data;
}

export async function fetchMonitoringReleaseHistory(token) {
  const res = await axios.get(`${API}/admin/monitoring/releases`, authHeaders(token));
  return res.data;
}

export async function fetchSiteCoverage(token) {
  const res = await axios.get(`${API}/admin/monitoring/site-coverage`, authHeaders(token));
  return res.data;
}

// ---------------------------------------------------------------------------
// Phase B — deterministic safe auto-recovery (monitoring_routes.py)
// ---------------------------------------------------------------------------
export async function fetchAutomationOverview(token) {
  const res = await axios.get(`${API}/admin/monitoring/automation-overview`, authHeaders(token));
  return res.data;
}

export async function approveRecovery(token, incidentId) {
  const res = await axios.post(`${API}/admin/monitoring/incidents/${incidentId}/approve-recovery`, {}, authHeaders(token));
  return res.data;
}

export async function declineRecovery(token, incidentId) {
  const res = await axios.post(`${API}/admin/monitoring/incidents/${incidentId}/decline-recovery`, {}, authHeaders(token));
  return res.data;
}

export async function pauseSiteAutomation(token, incidentId, reason) {
  const res = await axios.post(`${API}/admin/monitoring/incidents/${incidentId}/pause-site-automation`, null, {
    ...authHeaders(token), params: { reason: reason || "" },
  });
  return res.data;
}

export async function resumeSiteAutomation(token, websiteSlug) {
  const res = await axios.post(`${API}/admin/monitoring/site-policy/${websiteSlug}/resume`, {}, authHeaders(token));
  return res.data;
}

// ---------------------------------------------------------------------------
// Phase C — constrained AI-assisted diagnosis and repair (monitoring_routes.py).
// AI never gets a route of its own to production; every one of these calls a
// human-triggered admin action against repair_engine.py's own decision points.
// ---------------------------------------------------------------------------
export async function startRepair(token, incidentId, targetedTestPath) {
  const res = await axios.post(
    `${API}/admin/monitoring/incidents/${incidentId}/start-repair`,
    { targeted_test_path: targetedTestPath || null },
    authHeaders(token),
  );
  return res.data;
}

export async function fetchRepairRuns(token, incidentId) {
  const res = await axios.get(`${API}/admin/monitoring/incidents/${incidentId}/repair-runs`, authHeaders(token));
  return res.data;
}

export async function fetchRepairRun(token, repairRunId) {
  const res = await axios.get(`${API}/admin/monitoring/repair-runs/${repairRunId}`, authHeaders(token));
  return res.data;
}

export async function approveRepair(token, repairRunId) {
  const res = await axios.post(`${API}/admin/monitoring/repair-runs/${repairRunId}/approve`, {}, authHeaders(token));
  return res.data;
}

export async function declineRepair(token, repairRunId, reason) {
  const res = await axios.post(
    `${API}/admin/monitoring/repair-runs/${repairRunId}/decline`,
    { reason: reason || null },
    authHeaders(token),
  );
  return res.data;
}

export async function fetchBackupStatus(token) {
  const res = await axios.get(`${API}/admin/backups/status`, authHeaders(token));
  return res.data;
}

export async function fetchBackupList(token) {
  const res = await axios.get(`${API}/admin/backups`, authHeaders(token));
  return res.data;
}

export async function runManualBackup(token) {
  const res = await axios.post(`${API}/admin/backups/run`, {}, authHeaders(token));
  return res.data;
}

export async function verifyBackupRestore(token, backupId) {
  const res = await axios.post(`${API}/admin/backups/${backupId}/verify-restore`, {}, authHeaders(token));
  return res.data;
}

export async function fetchEmailStatus(token) {
  const res = await axios.get(`${API}/admin/email/status`, authHeaders(token));
  return res.data;
}
export async function previewPaymongoSampleEmail(token, market) {
  const res = await axios.post(`${API}/admin/email/paymongo-sample/preview`, { market }, authHeaders(token));
  return res.data;
}
export async function sendPaymongoSampleEmail(token, market) {
  const res = await axios.post(`${API}/admin/email/paymongo-sample/send`, { market }, authHeaders(token));
  return res.data;
}

export async function linkComplianceDocument(token, businessId, docId, kind, refId, note) {
  const res = await axios.post(`${API}/admin/billing/documents/${docId}/links`, { kind, ref_id: refId, note: note || undefined }, { ...authHeaders(token), params: { business_id: businessId } });
  return res.data;
}
export async function unlinkComplianceDocument(token, businessId, docId, kind, refId) {
  const res = await axios.delete(`${API}/admin/billing/documents/${docId}/links`, { ...authHeaders(token), params: { business_id: businessId, kind, ref_id: refId } });
  return res.data;
}

// --- Finance roles (capabilities reflect the backend's own enforcement) -----------
export async function fetchFinanceCapabilities(token) {
  const res = await axios.get(`${API}/admin/billing/me/finance-capabilities`, authHeaders(token));
  return res.data;
}

// --- Client-business source pipeline ---------------------------------------------
export async function fetchBusinessTransactions(token, businessId, kind) {
  const res = await axios.get(`${API}/admin/billing/business-transactions`, { ...authHeaders(token), params: { business_id: businessId, kind: kind || undefined } });
  return res.data;
}
export async function createBusinessTransaction(token, payload) {
  const res = await axios.post(`${API}/admin/billing/business-transactions`, payload, authHeaders(token));
  return res.data;
}
export async function reverseBusinessTransaction(token, businessId, txId, reason) {
  const res = await axios.post(`${API}/admin/billing/business-transactions/${txId}/reverse`, null, { ...authHeaders(token), params: { business_id: businessId, reason } });
  return res.data;
}
export async function importBusinessTransactions(token, businessId, csv) {
  const res = await axios.post(`${API}/admin/billing/business-transactions/import`, { business_id: businessId, csv }, authHeaders(token));
  return res.data;
}

// --- Reports catalog ---------------------------------------------------------------
export async function fetchReportsCatalog(token, businessId) {
  const res = await axios.get(`${API}/admin/billing/reports/catalog`, { ...authHeaders(token), params: { business_id: businessId } });
  return res.data;
}
export async function runCatalogReport(token, key, { businessId, period, year, fmt = "json", filters = {} }) {
  const cleaned = Object.fromEntries(Object.entries(filters).filter(([, v]) => v !== "" && v !== undefined && v !== null && v !== false));
  const res = await axios.get(`${API}/admin/billing/reports/run/${key}`, { ...authHeaders(token), params: { business_id: businessId, period: period || undefined, year: year || undefined, fmt, ...cleaned } });
  return res.data;
}

// --- Download packages --------------------------------------------------------------
export async function fetchPackageCatalog(token, businessId) {
  const res = await axios.get(`${API}/admin/billing/packages/catalog`, { ...authHeaders(token), params: { business_id: businessId } });
  return res.data;
}
export async function fetchPackagePreview(token, kind, businessId, { period, year, quarter } = {}) {
  const res = await axios.get(`${API}/admin/billing/packages/preview`, { ...authHeaders(token), params: { kind, business_id: businessId, period: period || undefined, year: year || undefined, quarter: quarter || undefined } });
  return res.data;
}
export async function buildPackage(token, kind, businessId, { period, year, quarter, fmt = "csv" } = {}) {
  const res = await axios.get(`${API}/admin/billing/packages/build`, { ...authHeaders(token), params: { kind, business_id: businessId, period: period || undefined, year: year || undefined, quarter: quarter || undefined, fmt } });
  return res.data;
}

// --- Open-period corrections (descriptive fields only; audited) ---------------------------
export async function patchBusinessTransaction(token, businessId, txId, payload) {
  const res = await axios.patch(`${API}/admin/billing/business-transactions/${txId}`, payload, { ...authHeaders(token), params: { business_id: businessId } });
  return res.data;
}
export async function patchExpense(token, businessId, expenseId, payload) {
  const res = await axios.patch(`${API}/admin/billing/expenses/${expenseId}`, payload, { ...authHeaders(token), params: { business_id: businessId } });
  return res.data;
}
